// CAIQ v4.0.3 Template - Consensus Assessments Initiative Questionnaire
// Cloud Security Alliance (CSA)
// Generated from official CAIQ v4.0.3 STAR Security Questionnaire

export interface CAIQQuestion {
  id: string
  domain: string
  question: string
  answerType: 'yes_no' | 'yes_no_explanation' | 'text'
  ccmControl: string
  ccmControlTitle?: string
  mapsTo?: {
    soc2?: string[]
    iso27001?: string[]
    nist?: string[]
    gdpr?: string[]
    pci?: string[]
  }
}

export interface CAIQDomain {
  id: string
  name: string
  description: string
  questions: CAIQQuestion[]
}

export const CAIQ_V4: CAIQDomain[] = [
  {
    id: 'A&A',
    name: 'Audit & Assurance',
    description: 'Audit & Assurance',
    questions: [
      {
        id: 'A&A-01.1',
        domain: 'A&A',
        question: 'Are audit and assurance policies, procedures, and standards established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'A&A-01',
        ccmControlTitle: 'Audit and Assurance Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'A&A-01.2',
        domain: 'A&A',
        question: 'Are audit and assurance policies, procedures, and standards reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'A&A-01',
        mapsTo: {}
      },
      {
        id: 'A&A-02.1',
        domain: 'A&A',
        question: 'Are independent audit and assurance assessments conducted according to relevant standards at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'A&A-02',
        ccmControlTitle: 'Independent Assessments',
        mapsTo: {}
      },
      {
        id: 'A&A-03.1',
        domain: 'A&A',
        question: 'Are independent audit and assurance assessments performed according to risk-based plans and policies?',
        answerType: 'yes_no_explanation',
        ccmControl: 'A&A-03',
        ccmControlTitle: 'Risk Based Planning Assessment',
        mapsTo: {}
      },
      {
        id: 'A&A-04.1',
        domain: 'A&A',
        question: 'Is compliance verified regarding all relevant standards, regulations, legal/contractual, and statutory requirements applicable to the audit?',
        answerType: 'yes_no_explanation',
        ccmControl: 'A&A-04',
        ccmControlTitle: 'Requirements Compliance',
        mapsTo: {}
      },
      {
        id: 'A&A-05.1',
        domain: 'A&A',
        question: 'Is an audit management process defined and implemented to support audit planning, risk analysis, security control assessments, conclusions, remediation schedules, report generation, and reviews of past reports and supporting evidence?',
        answerType: 'yes_no_explanation',
        ccmControl: 'A&A-05',
        ccmControlTitle: 'Audit Management Process',
        mapsTo: {}
      },
      {
        id: 'A&A-06.1',
        domain: 'A&A',
        question: 'Is a risk-based corrective action plan to remediate audit findings established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'A&A-06',
        ccmControlTitle: 'Remediation',
        mapsTo: {}
      },
      {
        id: 'A&A-06.2',
        domain: 'A&A',
        question: 'Is the remediation status of audit findings reviewed and reported to relevant stakeholders?',
        answerType: 'yes_no_explanation',
        ccmControl: 'A&A-06',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'AIS',
    name: 'Application & Interface Security',
    description: 'Application & Interface Security',
    questions: [
      {
        id: 'AIS-01.1',
        domain: 'AIS',
        question: 'Are application security policies and procedures established, documented, approved, communicated, applied, evaluated, and maintained to guide appropriate planning, delivery, and support of the organization\'s application security capabilities?',
        answerType: 'yes_no_explanation',
        ccmControl: 'AIS-01',
        ccmControlTitle: 'Application and Interface Security Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'AIS-01.2',
        domain: 'AIS',
        question: 'Are application security policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'AIS-01',
        mapsTo: {}
      },
      {
        id: 'AIS-02.1',
        domain: 'AIS',
        question: 'Are baseline requirements to secure different applications established, documented, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'AIS-02',
        ccmControlTitle: 'Application Security Baseline Requirements',
        mapsTo: {}
      },
      {
        id: 'AIS-03.1',
        domain: 'AIS',
        question: 'Are technical and operational metrics defined and implemented according to business objectives, security requirements, and compliance obligations?',
        answerType: 'yes_no_explanation',
        ccmControl: 'AIS-03',
        ccmControlTitle: 'Application Security Metrics',
        mapsTo: {}
      },
      {
        id: 'AIS-04.1',
        domain: 'AIS',
        question: 'Is an SDLC process defined and implemented for application design, development, deployment, and operation per organizationally designed security requirements?',
        answerType: 'yes_no_explanation',
        ccmControl: 'AIS-04',
        ccmControlTitle: 'Secure Application Design and Development',
        mapsTo: {}
      },
      {
        id: 'AIS-05.1',
        domain: 'AIS',
        question: 'Does the testing strategy outline criteria to accept new information systems, upgrades, and new versions while ensuring application security, compliance adherence, and organizational speed of delivery goals?',
        answerType: 'yes_no_explanation',
        ccmControl: 'AIS-05',
        ccmControlTitle: 'Automated Application Security Testing',
        mapsTo: {}
      },
      {
        id: 'AIS-05.2',
        domain: 'AIS',
        question: 'Is testing automated when applicable and possible?',
        answerType: 'yes_no_explanation',
        ccmControl: 'AIS-05',
        mapsTo: {}
      },
      {
        id: 'AIS-06.1',
        domain: 'AIS',
        question: 'Are strategies and capabilities established and implemented to deploy application code in a secure, standardized, and compliant manner?',
        answerType: 'yes_no_explanation',
        ccmControl: 'AIS-06',
        ccmControlTitle: 'Automated Secure Application Deployment',
        mapsTo: {}
      },
      {
        id: 'AIS-06.2',
        domain: 'AIS',
        question: 'Is the deployment and integration of application code automated where possible?',
        answerType: 'yes_no_explanation',
        ccmControl: 'AIS-06',
        mapsTo: {}
      },
      {
        id: 'AIS-07.1',
        domain: 'AIS',
        question: 'Are application security vulnerabilities remediated following defined processes?',
        answerType: 'yes_no_explanation',
        ccmControl: 'AIS-07',
        ccmControlTitle: 'Application Vulnerability Remediation',
        mapsTo: {}
      },
      {
        id: 'AIS-07.2',
        domain: 'AIS',
        question: 'Is the remediation of application security vulnerabilities automated when possible?',
        answerType: 'yes_no_explanation',
        ccmControl: 'AIS-07',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'BCR',
    name: 'Business Continuity Management and Operational Resilience',
    description: 'Business Continuity Management and Operational Resilience',
    questions: [
      {
        id: 'BCR-01.1',
        domain: 'BCR',
        question: 'Are business continuity management and operational resilience policies and procedures established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-01',
        ccmControlTitle: 'Business Continuity Management Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'BCR-01.2',
        domain: 'BCR',
        question: 'Are the policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-01',
        mapsTo: {}
      },
      {
        id: 'BCR-02.1',
        domain: 'BCR',
        question: 'Are criteria for developing business continuity and operational resiliency strategies and capabilities established based on business disruption and risk impacts?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-02',
        ccmControlTitle: 'Risk Assessment and Impact Analysis',
        mapsTo: {}
      },
      {
        id: 'BCR-03.1',
        domain: 'BCR',
        question: 'Are strategies developed to reduce the impact of, withstand, and recover from business disruptions in accordance with risk appetite?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-03',
        ccmControlTitle: 'Business Continuity Strategy',
        mapsTo: {}
      },
      {
        id: 'BCR-04.1',
        domain: 'BCR',
        question: 'Are operational resilience strategies and capability results incorporated to establish, document, approve, communicate, apply, evaluate, and maintain a business continuity plan?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-04',
        ccmControlTitle: 'Business Continuity Planning',
        mapsTo: {}
      },
      {
        id: 'BCR-05.1',
        domain: 'BCR',
        question: 'Is relevant documentation developed, identified, and acquired to support business continuity and operational resilience plans?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-05',
        ccmControlTitle: 'Documentation',
        mapsTo: {}
      },
      {
        id: 'BCR-05.2',
        domain: 'BCR',
        question: 'Is business continuity and operational resilience documentation available to authorized stakeholders?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-05',
        mapsTo: {}
      },
      {
        id: 'BCR-05.3',
        domain: 'BCR',
        question: 'Is business continuity and operational resilience documentation reviewed periodically?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-05',
        mapsTo: {}
      },
      {
        id: 'BCR-06.1',
        domain: 'BCR',
        question: 'Are the business continuity and operational resilience plans exercised and tested at least annually and when significant changes occur?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-06',
        ccmControlTitle: 'Business Continuity Exercises',
        mapsTo: {}
      },
      {
        id: 'BCR-07.1',
        domain: 'BCR',
        question: 'Do business continuity and resilience procedures establish communication with stakeholders and participants?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-07',
        ccmControlTitle: 'Communication',
        mapsTo: {}
      },
      {
        id: 'BCR-08.1',
        domain: 'BCR',
        question: 'Is cloud data periodically backed up?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-08',
        ccmControlTitle: 'Backup',
        mapsTo: {}
      },
      {
        id: 'BCR-08.2',
        domain: 'BCR',
        question: 'Is the confidentiality, integrity, and availability of backup data ensured?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-08',
        mapsTo: {}
      },
      {
        id: 'BCR-08.3',
        domain: 'BCR',
        question: 'Can backups be restored appropriately for resiliency?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-08',
        mapsTo: {}
      },
      {
        id: 'BCR-09.1',
        domain: 'BCR',
        question: 'Is a disaster response plan established, documented, approved, applied, evaluated, and maintained to ensure recovery from natural and man-made disasters?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-09',
        ccmControlTitle: 'Disaster Response Plan',
        mapsTo: {}
      },
      {
        id: 'BCR-09.2',
        domain: 'BCR',
        question: 'Is the disaster response plan updated at least annually, and when significant changes occur?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-09',
        mapsTo: {}
      },
      {
        id: 'BCR-10.1',
        domain: 'BCR',
        question: 'Is the disaster response plan exercised annually or when significant changes occur?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-10',
        ccmControlTitle: 'Response Plan Exercise',
        mapsTo: {}
      },
      {
        id: 'BCR-10.2',
        domain: 'BCR',
        question: 'Are local emergency authorities included, if possible, in the exercise?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-10',
        mapsTo: {}
      },
      {
        id: 'BCR-11.1',
        domain: 'BCR',
        question: 'Is business-critical equipment supplemented with redundant equipment independently located at a reasonable minimum distance in accordance with applicable industry standards?',
        answerType: 'yes_no_explanation',
        ccmControl: 'BCR-11',
        ccmControlTitle: 'Equipment Redundancy',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'CCC',
    name: 'Change Control and Configuration Management',
    description: 'Change Control and Configuration Management',
    questions: [
      {
        id: 'CCC-01.1',
        domain: 'CCC',
        question: 'Are risk management policies and procedures associated with changing organizational assets including applications, systems, infrastructure, configuration, etc., established, documented, approved, communicated, applied, evaluated and maintained (regardless of whether asset management is internal or external)?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CCC-01',
        ccmControlTitle: 'Change Management Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'CCC-01.2',
        domain: 'CCC',
        question: 'Are the policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CCC-01',
        mapsTo: {}
      },
      {
        id: 'CCC-02.1',
        domain: 'CCC',
        question: 'Is a defined quality change control, approval and testing process (with established baselines, testing, and release standards) followed?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CCC-02',
        ccmControlTitle: 'Quality Testing',
        mapsTo: {}
      },
      {
        id: 'CCC-03.1',
        domain: 'CCC',
        question: 'Are risks associated with changing organizational assets (including applications, systems, infrastructure, configuration, etc.) managed, regardless of whether asset management occurs internally or externally (i.e., outsourced)?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CCC-03',
        ccmControlTitle: 'Change Management Technology',
        mapsTo: {}
      },
      {
        id: 'CCC-04.1',
        domain: 'CCC',
        question: 'Is the unauthorized addition, removal, update, and management of organization assets restricted?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CCC-04',
        ccmControlTitle: 'Unauthorized Change Protection',
        mapsTo: {}
      },
      {
        id: 'CCC-05.1',
        domain: 'CCC',
        question: 'Are provisions to limit changes that directly impact CSC-owned environments and require tenants to authorize requests explicitly included within the service level agreements (SLAs) between CSPs and CSCs?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CCC-05',
        ccmControlTitle: 'Change Agreements',
        mapsTo: {}
      },
      {
        id: 'CCC-06.1',
        domain: 'CCC',
        question: 'Are change management baselines established for all relevant authorized changes on organizational assets?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CCC-06',
        ccmControlTitle: 'Change Management Baseline',
        mapsTo: {}
      },
      {
        id: 'CCC-07.1',
        domain: 'CCC',
        question: 'Are detection measures implemented with proactive notification if changes deviate from established baselines?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CCC-07',
        ccmControlTitle: 'Detection of Baseline Deviation',
        mapsTo: {}
      },
      {
        id: 'CCC-08.1',
        domain: 'CCC',
        question: 'Is a procedure implemented to manage exceptions, including emergencies, in the change and configuration process?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CCC-08',
        ccmControlTitle: 'Exception Management',
        mapsTo: {}
      },
      {
        id: 'CCC-08.2',
        domain: 'CCC',
        question: '\'Is the procedure aligned with the requirements of the GRC-04: Policy Exception Process?\'',
        answerType: 'yes_no_explanation',
        ccmControl: 'CCC-08',
        mapsTo: {}
      },
      {
        id: 'CCC-09.1',
        domain: 'CCC',
        question: 'Is a process to proactively roll back changes to a previously known "good state" defined and implemented in case of errors or security concerns?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CCC-09',
        ccmControlTitle: 'Change Restoration',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'CEK',
    name: 'Cryptography, Encryption & Key Management',
    description: 'Cryptography, Encryption & Key Management',
    questions: [
      {
        id: 'CEK-01.1',
        domain: 'CEK',
        question: 'Are cryptography, encryption, and key management policies and procedures established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-01',
        ccmControlTitle: 'Encryption and Key Management Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'CEK-01.2',
        domain: 'CEK',
        question: 'Are cryptography, encryption, and key management policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-01',
        mapsTo: {}
      },
      {
        id: 'CEK-02.1',
        domain: 'CEK',
        question: 'Are cryptography, encryption, and key management roles and responsibilities defined and implemented?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-02',
        ccmControlTitle: 'CEK Roles and Responsibilities',
        mapsTo: {}
      },
      {
        id: 'CEK-03.1',
        domain: 'CEK',
        question: 'Are data at-rest and in-transit cryptographically protected using cryptographic libraries certified to approved standards?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-03',
        ccmControlTitle: 'Data Encryption',
        mapsTo: {}
      },
      {
        id: 'CEK-04.1',
        domain: 'CEK',
        question: 'Are appropriate data protection encryption algorithms used that consider data classification, associated risks, and encryption technology usability?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-04',
        ccmControlTitle: 'Encryption Algorithm',
        mapsTo: {}
      },
      {
        id: 'CEK-05.1',
        domain: 'CEK',
        question: 'Are standard change management procedures established to review, approve, implement and communicate cryptography, encryption, and key management technology changes that accommodate internal and external sources?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-05',
        ccmControlTitle: 'Encryption Change Management',
        mapsTo: {}
      },
      {
        id: 'CEK-06.1',
        domain: 'CEK',
        question: 'Are changes to cryptography-, encryption- and key management-related systems, policies, and procedures, managed and adopted in a manner that fully accounts for downstream effects of proposed changes, including residual risk, cost, and benefits analysis?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-06',
        ccmControlTitle: 'Encryption Change Cost Benefit Analysis',
        mapsTo: {}
      },
      {
        id: 'CEK-07.1',
        domain: 'CEK',
        question: 'Is a cryptography, encryption, and key management risk program established and maintained that includes risk assessment, risk treatment, risk context, monitoring, and feedback provisions?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-07',
        ccmControlTitle: 'Encryption Risk Management',
        mapsTo: {}
      },
      {
        id: 'CEK-08.1',
        domain: 'CEK',
        question: 'Are CSPs providing CSCs with the capacity to manage their own data encryption keys?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-08',
        ccmControlTitle: 'CSC Key Management Capability',
        mapsTo: {}
      },
      {
        id: 'CEK-09.1',
        domain: 'CEK',
        question: 'Are encryption and key management systems, policies, and processes audited with a frequency proportional to the system\'s risk exposure, and after any security event?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-09',
        ccmControlTitle: 'Encryption and Key Management Audit',
        mapsTo: {}
      },
      {
        id: 'CEK-09.2',
        domain: 'CEK',
        question: 'Are encryption and key management systems, policies, and processes audited (preferably continuously but at least annually)?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-09',
        mapsTo: {}
      },
      {
        id: 'CEK-10.1',
        domain: 'CEK',
        question: 'Are cryptographic keys generated using industry-accepted and approved cryptographic libraries that specify algorithm strength and random number generator specifications?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-10',
        ccmControlTitle: 'Key Generation',
        mapsTo: {}
      },
      {
        id: 'CEK-11.1',
        domain: 'CEK',
        question: 'Are private keys provisioned for a unique purpose managed, and is cryptography secret?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-11',
        ccmControlTitle: 'Key Purpose',
        mapsTo: {}
      },
      {
        id: 'CEK-12.1',
        domain: 'CEK',
        question: 'Are cryptographic keys rotated based on a cryptoperiod calculated while considering information disclosure risks and legal and regulatory requirements?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-12',
        ccmControlTitle: 'Key Rotation',
        mapsTo: {}
      },
      {
        id: 'CEK-13.1',
        domain: 'CEK',
        question: 'Are cryptographic keys revoked and removed before the end of the established cryptoperiod (when a key is compromised, or an entity is no longer part of the organization) per defined, implemented, and evaluated processes, procedures, and technical measures to include legal and regulatory requirement provisions?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-13',
        ccmControlTitle: 'Key Revocation',
        mapsTo: {}
      },
      {
        id: 'CEK-14.1',
        domain: 'CEK',
        question: 'Are processes, procedures and technical measures to destroy unneeded keys defined, implemented and evaluated to address key destruction outside secure environments, revocation of keys stored in hardware security modules (HSMs), and include applicable legal and regulatory requirement provisions?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-14',
        ccmControlTitle: 'Key Destruction',
        mapsTo: {}
      },
      {
        id: 'CEK-15.1',
        domain: 'CEK',
        question: 'Are processes, procedures, and technical measures to create keys in a pre-activated state (i.e., when they have been generated but not authorized for use) being defined, implemented, and evaluated to include legal and regulatory requirement provisions?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-15',
        ccmControlTitle: 'Key Activation',
        mapsTo: {}
      },
      {
        id: 'CEK-16.1',
        domain: 'CEK',
        question: 'Are processes, procedures, and technical measures to monitor, review and approve key transitions (e.g., from any state to/from suspension) being defined, implemented, and evaluated to include legal and regulatory requirement provisions?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-16',
        ccmControlTitle: 'Key Suspension',
        mapsTo: {}
      },
      {
        id: 'CEK-17.1',
        domain: 'CEK',
        question: 'Are processes, procedures, and technical measures to deactivate keys (at the time of their expiration date) being defined, implemented, and evaluated to include legal and regulatory requirement provisions?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-17',
        ccmControlTitle: 'Key Deactivation',
        mapsTo: {}
      },
      {
        id: 'CEK-18.1',
        domain: 'CEK',
        question: 'Are processes, procedures, and technical measures to manage archived keys in a secure repository (requiring least privilege access) being defined, implemented, and evaluated to include legal and regulatory requirement provisions?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-18',
        ccmControlTitle: 'Key Archival',
        mapsTo: {}
      },
      {
        id: 'CEK-19.1',
        domain: 'CEK',
        question: 'Are processes, procedures, and technical measures to use compromised keys to encrypt information in specific scenarios (e.g., only in controlled circumstances and thereafter only for data decryption and never for encryption) defined, implemented, and evaluated to include legal and regulatory requirement provisions?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-19',
        ccmControlTitle: 'Key Compromise',
        mapsTo: {}
      },
      {
        id: 'CEK-20.1',
        domain: 'CEK',
        question: 'Are processes, procedures, and technical measures to assess operational continuity risks (versus the risk of losing control of keying material and exposing protected data) being defined, implemented, and evaluated to include legal and regulatory requirement provisions?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-20',
        ccmControlTitle: 'Key Recovery',
        mapsTo: {}
      },
      {
        id: 'CEK-21.1',
        domain: 'CEK',
        question: 'Are key management system processes, procedures, and technical measures being defined, implemented, and evaluated to track and report all cryptographic materials and status changes that include legal and regulatory requirements provisions?',
        answerType: 'yes_no_explanation',
        ccmControl: 'CEK-21',
        ccmControlTitle: 'Key Inventory Management',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'DCS',
    name: 'Datacenter Security',
    description: 'Datacenter Security',
    questions: [
      {
        id: 'DCS-01.1',
        domain: 'DCS',
        question: 'Are policies and procedures for the secure disposal of equipment used outside the organization\'s premises established, documented, approved, communicated, enforced, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-01',
        ccmControlTitle: 'Off-Site Equipment Disposal Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'DCS-01.2',
        domain: 'DCS',
        question: 'Is a data destruction procedure applied that renders information recovery information impossible if equipment is not physically destroyed?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-01',
        mapsTo: {}
      },
      {
        id: 'DCS-01.3',
        domain: 'DCS',
        question: 'Are policies and procedures for the secure disposal of equipment used outside the organization\'s premises reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-01',
        mapsTo: {}
      },
      {
        id: 'DCS-02.1',
        domain: 'DCS',
        question: 'Are policies and procedures for the relocation or transfer of hardware, software, or data/information to an offsite or alternate location established, documented, approved, communicated, implemented, enforced, maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-02',
        ccmControlTitle: 'Off-Site Transfer Authorization Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'DCS-02.2',
        domain: 'DCS',
        question: 'Does a relocation or transfer request require written or cryptographically verifiable authorization?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-02',
        mapsTo: {}
      },
      {
        id: 'DCS-02.3',
        domain: 'DCS',
        question: 'Are policies and procedures for the relocation or transfer of hardware, software, or data/information to an offsite or alternate location reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-02',
        mapsTo: {}
      },
      {
        id: 'DCS-03.1',
        domain: 'DCS',
        question: 'Are policies and procedures for maintaining a safe and secure working environment (in offices, rooms, and facilities) established, documented, approved, communicated, enforced, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-03',
        ccmControlTitle: 'Secure Area Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'DCS-03.2',
        domain: 'DCS',
        question: 'Are policies and procedures for maintaining safe, secure working environments (e.g., offices, rooms) reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-03',
        mapsTo: {}
      },
      {
        id: 'DCS-04.1',
        domain: 'DCS',
        question: 'Are policies and procedures for the secure transportation of physical media established, documented, approved, communicated, enforced, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-04',
        ccmControlTitle: 'Secure Media Transportation Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'DCS-04.2',
        domain: 'DCS',
        question: 'Are policies and procedures for the secure transportation of physical media reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-04',
        mapsTo: {}
      },
      {
        id: 'DCS-05.1',
        domain: 'DCS',
        question: 'Is the classification and documentation of physical and logical assets based on the organizational business risk?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-05',
        ccmControlTitle: 'Assets Classification',
        mapsTo: {}
      },
      {
        id: 'DCS-06.1',
        domain: 'DCS',
        question: 'Are all relevant physical and logical assets at all CSP sites cataloged and tracked within a secured system?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-06',
        ccmControlTitle: 'Assets Cataloguing and Tracking',
        mapsTo: {}
      },
      {
        id: 'DCS-07.1',
        domain: 'DCS',
        question: 'Are physical security perimeters implemented to safeguard personnel, data, and information systems?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-07',
        ccmControlTitle: 'Controlled Access Points',
        mapsTo: {}
      },
      {
        id: 'DCS-07.2',
        domain: 'DCS',
        question: 'Are physical security perimeters established between administrative and business areas, data storage, and processing facilities?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-07',
        mapsTo: {}
      },
      {
        id: 'DCS-08.1',
        domain: 'DCS',
        question: 'Is equipment identification used as a method for connection authentication?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-08',
        ccmControlTitle: 'Equipment Identification',
        mapsTo: {}
      },
      {
        id: 'DCS-09.1',
        domain: 'DCS',
        question: 'Are solely authorized personnel able to access secure areas, with all ingress and egress areas restricted, documented, and monitored by physical access control mechanisms?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-09',
        ccmControlTitle: 'Secure Area Authorization',
        mapsTo: {}
      },
      {
        id: 'DCS-09.2',
        domain: 'DCS',
        question: 'Are access control records retained periodically, as deemed appropriate by the organization?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-09',
        mapsTo: {}
      },
      {
        id: 'DCS-10.1',
        domain: 'DCS',
        question: 'Are external perimeter datacenter surveillance systems and surveillance systems at all ingress and egress points implemented, maintained, and operated?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-10',
        ccmControlTitle: 'Surveillance System',
        mapsTo: {}
      },
      {
        id: 'DCS-11.1',
        domain: 'DCS',
        question: 'Are datacenter personnel trained to respond to unauthorized access or egress attempts?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-11',
        ccmControlTitle: 'Unauthorized Access Response Training',
        mapsTo: {}
      },
      {
        id: 'DCS-12.1',
        domain: 'DCS',
        question: 'Are processes, procedures, and technical measures defined, implemented, and evaluated to ensure risk-based protection of power and telecommunication cables from interception, interference, or damage threats at all facilities, offices, and rooms?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-12',
        ccmControlTitle: 'Cabling Security',
        mapsTo: {}
      },
      {
        id: 'DCS-13.1',
        domain: 'DCS',
        question: 'Are data center environmental control systems designed to monitor, maintain, and test that on-site temperature and humidity conditions fall within accepted industry standards effectively implemented and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-13',
        ccmControlTitle: 'Environmental Systems',
        mapsTo: {}
      },
      {
        id: 'DCS-14.1',
        domain: 'DCS',
        question: 'Are utility services secured, monitored, maintained, and tested at planned intervals for continual effectiveness?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-14',
        ccmControlTitle: 'Secure Utilities',
        mapsTo: {}
      },
      {
        id: 'DCS-15.1',
        domain: 'DCS',
        question: 'Is business-critical equipment segregated from locations subject to a high probability of environmental risk events?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DCS-15',
        ccmControlTitle: 'Equipment Location',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'DSP',
    name: 'Data Security and Privacy Lifecycle Management',
    description: 'Data Security and Privacy Lifecycle Management',
    questions: [
      {
        id: 'DSP-01.1',
        domain: 'DSP',
        question: 'Are policies and procedures established, documented, approved, communicated, enforced, evaluated, and maintained for the classification, protection, and handling of data throughout its lifecycle according to all applicable laws and regulations, standards, and risk level?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-01',
        ccmControlTitle: 'Security and Privacy Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'DSP-01.2',
        domain: 'DSP',
        question: 'Are data security and privacy policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-01',
        mapsTo: {}
      },
      {
        id: 'DSP-02.1',
        domain: 'DSP',
        question: 'Are industry-accepted methods applied for secure data disposal from storage media so information is not recoverable by any forensic means?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-02',
        ccmControlTitle: 'Secure Disposal',
        mapsTo: {}
      },
      {
        id: 'DSP-03.1',
        domain: 'DSP',
        question: 'Is a data inventory created and maintained for sensitive and personal information (at a minimum)?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-03',
        ccmControlTitle: 'Data Inventory',
        mapsTo: {}
      },
      {
        id: 'DSP-04.1',
        domain: 'DSP',
        question: 'Is data classified according to type and sensitivity levels?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-04',
        ccmControlTitle: 'Data Classification',
        mapsTo: {}
      },
      {
        id: 'DSP-05.1',
        domain: 'DSP',
        question: 'Is data flow documentation created to identify what data is processed and where it is stored and transmitted?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-05',
        ccmControlTitle: 'Data Flow Documentation',
        mapsTo: {}
      },
      {
        id: 'DSP-05.2',
        domain: 'DSP',
        question: 'Is data flow documentation reviewed at defined intervals, at least annually, and after any change?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-05',
        mapsTo: {}
      },
      {
        id: 'DSP-06.1',
        domain: 'DSP',
        question: 'Is the ownership and stewardship of all relevant personal and sensitive data documented?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-06',
        ccmControlTitle: 'Data Ownership and Stewardship',
        mapsTo: {}
      },
      {
        id: 'DSP-06.2',
        domain: 'DSP',
        question: 'Is data ownership and stewardship documentation reviewed at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-06',
        mapsTo: {}
      },
      {
        id: 'DSP-07.1',
        domain: 'DSP',
        question: 'Are systems, products, and business practices based on security principles by design and per industry best practices?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-07',
        ccmControlTitle: 'Data Protection by Design and Default',
        mapsTo: {}
      },
      {
        id: 'DSP-08.1',
        domain: 'DSP',
        question: 'Are systems, products, and business practices based on privacy principles by design and according to industry best practices?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-08',
        ccmControlTitle: 'Data Privacy by Design and Default',
        mapsTo: {}
      },
      {
        id: 'DSP-08.2',
        domain: 'DSP',
        question: 'Are systems\' privacy settings configured by default and according to all applicable laws and regulations?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-08',
        mapsTo: {}
      },
      {
        id: 'DSP-09.1',
        domain: 'DSP',
        question: 'Is a data protection impact assessment (DPIA) conducted when processing personal data and evaluating the origin, nature, particularity, and severity of risks according to any applicable laws, regulations and industry best practices?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-09',
        ccmControlTitle: 'Data Protection Impact Assessment',
        mapsTo: {}
      },
      {
        id: 'DSP-10.1',
        domain: 'DSP',
        question: 'Are processes, procedures, and technical measures defined, implemented, and evaluated to ensure any transfer of personal or sensitive data is protected from unauthorized access and only processed within scope (as permitted by respective laws and regulations)?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-10',
        ccmControlTitle: 'Sensitive Data Transfer',
        mapsTo: {}
      },
      {
        id: 'DSP-11.1',
        domain: 'DSP',
        question: 'Are processes, procedures, and technical measures defined, implemented, and evaluated to enable data subjects to request access to, modify, or delete personal data (per applicable laws and regulations)?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-11',
        ccmControlTitle: 'Personal Data Access, Reversal, Rectification and Deletion',
        mapsTo: {}
      },
      {
        id: 'DSP-12.1',
        domain: 'DSP',
        question: 'Are processes, procedures, and technical measures defined, implemented, and evaluated to ensure personal data is processed (per applicable laws and regulations and for the purposes declared to the data subject)?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-12',
        ccmControlTitle: 'Limitation of Purpose in Personal Data Processing',
        mapsTo: {}
      },
      {
        id: 'DSP-13.1',
        domain: 'DSP',
        question: 'Are processes, procedures, and technical measures defined, implemented, and evaluated for the transfer and sub-processing of personal data within the service supply chain (according to any applicable laws and regulations)?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-13',
        ccmControlTitle: 'Personal Data Sub-processing',
        mapsTo: {}
      },
      {
        id: 'DSP-14.1',
        domain: 'DSP',
        question: 'Are processes, procedures, and technical measures defined, implemented, and evaluated to disclose details to the data owner of any personal or sensitive data access by sub-processors before processing initiation?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-14',
        ccmControlTitle: 'Disclosure of Data Sub-processors',
        mapsTo: {}
      },
      {
        id: 'DSP-15.1',
        domain: 'DSP',
        question: 'Is authorization from data owners obtained, and the associated risk managed, before replicating or using production data in non-production environments?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-15',
        ccmControlTitle: 'Limitation of Production Data Use',
        mapsTo: {}
      },
      {
        id: 'DSP-16.1',
        domain: 'DSP',
        question: 'Do data retention, archiving, and deletion practices follow business requirements, applicable laws, and regulations?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-16',
        ccmControlTitle: 'Data Retention and Deletion',
        mapsTo: {}
      },
      {
        id: 'DSP-17.1',
        domain: 'DSP',
        question: 'Are processes, procedures, and technical measures defined and implemented to protect sensitive data throughout its lifecycle?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-17',
        ccmControlTitle: 'Sensitive Data Protection',
        mapsTo: {}
      },
      {
        id: 'DSP-18.1',
        domain: 'DSP',
        question: 'Does the CSP have in place, and describe to CSCs, the procedure to manage and respond to requests for disclosure of Personal Data by Law Enforcement Authorities according to applicable laws and regulations?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-18',
        ccmControlTitle: 'Disclosure Notification',
        mapsTo: {}
      },
      {
        id: 'DSP-18.2',
        domain: 'DSP',
        question: 'Does the CSP give special attention to the notification procedure to interested CSCs, unless otherwise prohibited, such as a prohibition under criminal law to preserve confidentiality of a law enforcement investigation?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-18',
        mapsTo: {}
      },
      {
        id: 'DSP-19.1',
        domain: 'DSP',
        question: 'Are processes, procedures, and technical measures defined and implemented to specify and document physical data locations, including locales where data is processed or backed up?',
        answerType: 'yes_no_explanation',
        ccmControl: 'DSP-19',
        ccmControlTitle: 'Data Location',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'GRC',
    name: 'Governance, Risk and Compliance',
    description: 'Governance, Risk and Compliance',
    questions: [
      {
        id: 'GRC-01.1',
        domain: 'GRC',
        question: 'Are information governance program policies and procedures sponsored by organizational leadership established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'GRC-01',
        ccmControlTitle: 'Governance Program Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'GRC-01.2',
        domain: 'GRC',
        question: 'Are the policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'GRC-01',
        mapsTo: {}
      },
      {
        id: 'GRC-02.1',
        domain: 'GRC',
        question: 'Is there an established formal, documented, and leadership-sponsored enterprise risk management (ERM) program that includes policies and procedures for identification, evaluation, ownership, treatment, and acceptance of cloud security and privacy risks?',
        answerType: 'yes_no_explanation',
        ccmControl: 'GRC-02',
        ccmControlTitle: 'Risk Management Program',
        mapsTo: {}
      },
      {
        id: 'GRC-03.1',
        domain: 'GRC',
        question: 'Are all relevant organizational policies and associated procedures reviewed at least annually, or when a substantial organizational change occurs?',
        answerType: 'yes_no_explanation',
        ccmControl: 'GRC-03',
        ccmControlTitle: 'Organizational Policy Reviews',
        mapsTo: {}
      },
      {
        id: 'GRC-04.1',
        domain: 'GRC',
        question: 'Is an approved exception process mandated by the governance program established and followed whenever a deviation from an established policy occurs?',
        answerType: 'yes_no_explanation',
        ccmControl: 'GRC-04',
        ccmControlTitle: 'Policy Exception Process',
        mapsTo: {}
      },
      {
        id: 'GRC-05.1',
        domain: 'GRC',
        question: 'Has an information security program (including programs of all relevant CCM domains) been developed and implemented?',
        answerType: 'yes_no_explanation',
        ccmControl: 'GRC-05',
        ccmControlTitle: 'Information Security Program',
        mapsTo: {}
      },
      {
        id: 'GRC-06.1',
        domain: 'GRC',
        question: 'Are roles and responsibilities for planning, implementing, operating, assessing, and improving governance programs defined and documented?',
        answerType: 'yes_no_explanation',
        ccmControl: 'GRC-06',
        ccmControlTitle: 'Governance Responsibility Model',
        mapsTo: {}
      },
      {
        id: 'GRC-07.1',
        domain: 'GRC',
        question: 'Are all relevant standards, regulations, legal/contractual, and statutory requirements applicable to your organization identified and documented?',
        answerType: 'yes_no_explanation',
        ccmControl: 'GRC-07',
        ccmControlTitle: 'Information System Regulatory Mapping',
        mapsTo: {}
      },
      {
        id: 'GRC-08.1',
        domain: 'GRC',
        question: 'Is contact established and maintained with cloud-related special interest groups and other relevant entities?',
        answerType: 'yes_no_explanation',
        ccmControl: 'GRC-08',
        ccmControlTitle: 'Special Interest Groups',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'HRS',
    name: 'Human Resources',
    description: 'Human Resources',
    questions: [
      {
        id: 'HRS-01.1',
        domain: 'HRS',
        question: 'Are background verification policies and procedures of all new employees (including but not limited to remote employees, contractors, and third parties) established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-01',
        ccmControlTitle: 'Background Screening Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'HRS-01.2',
        domain: 'HRS',
        question: 'Are background verification policies and procedures designed according to local laws, regulations, ethics, and contractual constraints and proportional to the data classification to be accessed, business requirements, and acceptable risk?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-01',
        mapsTo: {}
      },
      {
        id: 'HRS-01.3',
        domain: 'HRS',
        question: 'Are background verification policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-01',
        mapsTo: {}
      },
      {
        id: 'HRS-02.1',
        domain: 'HRS',
        question: 'Are policies and procedures for defining allowances and conditions for the acceptable use of organizationally-owned or managed assets established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-02',
        ccmControlTitle: 'Acceptable Use of Technology Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'HRS-02.2',
        domain: 'HRS',
        question: 'Are the policies and procedures for defining allowances and conditions for the acceptable use of organizationally-owned or managed assets reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-02',
        mapsTo: {}
      },
      {
        id: 'HRS-03.1',
        domain: 'HRS',
        question: 'Are policies and procedures requiring unattended workspaces to conceal confidential data established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-03',
        ccmControlTitle: 'Clean Desk Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'HRS-03.2',
        domain: 'HRS',
        question: 'Are policies and procedures requiring unattended workspaces to conceal confidential data reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-03',
        mapsTo: {}
      },
      {
        id: 'HRS-04.1',
        domain: 'HRS',
        question: 'Are policies and procedures to protect information accessed, processed, or stored at remote sites and locations established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-04',
        ccmControlTitle: 'Remote and Home Working Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'HRS-04.2',
        domain: 'HRS',
        question: 'Are policies and procedures to protect information accessed, processed, or stored at remote sites and locations reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-04',
        mapsTo: {}
      },
      {
        id: 'HRS-05.1',
        domain: 'HRS',
        question: 'Are return procedures of organizationally-owned assets by terminated employees established and documented?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-05',
        ccmControlTitle: 'Asset returns',
        mapsTo: {}
      },
      {
        id: 'HRS-06.1',
        domain: 'HRS',
        question: 'Are procedures outlining the roles and responsibilities concerning changes in employment established, documented, and communicated to all personnel?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-06',
        ccmControlTitle: 'Employment Termination',
        mapsTo: {}
      },
      {
        id: 'HRS-07.1',
        domain: 'HRS',
        question: 'Are employees required to sign an employment agreement before gaining access to organizational information systems, resources, and assets?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-07',
        ccmControlTitle: 'Employment Agreement Process',
        mapsTo: {}
      },
      {
        id: 'HRS-08.1',
        domain: 'HRS',
        question: 'Are provisions and/or terms for adherence to established information governance and security policies included within employment agreements?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-08',
        ccmControlTitle: 'Employment Agreement Content',
        mapsTo: {}
      },
      {
        id: 'HRS-09.1',
        domain: 'HRS',
        question: 'Are employee roles and responsibilities relating to information assets and security documented and communicated?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-09',
        ccmControlTitle: 'Personnel Roles and Responsibilities',
        mapsTo: {}
      },
      {
        id: 'HRS-10.1',
        domain: 'HRS',
        question: 'Are requirements for non-disclosure/confidentiality agreements reflecting organizational data protection needs and operational details identified, documented, and reviewed at planned intervals?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-10',
        ccmControlTitle: 'Non-Disclosure Agreements',
        mapsTo: {}
      },
      {
        id: 'HRS-11.1',
        domain: 'HRS',
        question: 'Is a security awareness training program for all employees of the organization established, documented, approved, communicated, applied, evaluated and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-11',
        ccmControlTitle: 'Security Awareness Training',
        mapsTo: {}
      },
      {
        id: 'HRS-11.2',
        domain: 'HRS',
        question: 'Are regular security awareness training updates provided?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-11',
        mapsTo: {}
      },
      {
        id: 'HRS-12.1',
        domain: 'HRS',
        question: 'Are all employees granted access to sensitive organizational and personal data provided with appropriate security awareness training?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-12',
        ccmControlTitle: 'Personal and Sensitive Data Awareness and Training',
        mapsTo: {}
      },
      {
        id: 'HRS-12.2',
        domain: 'HRS',
        question: 'Are all employees granted access to sensitive organizational and personal data provided with regular updates in procedures, processes, and policies relating to their professional function?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-12',
        mapsTo: {}
      },
      {
        id: 'HRS-13.1',
        domain: 'HRS',
        question: 'Are employees notified of their roles and responsibilities to maintain awareness and compliance with established policies, procedures, and applicable legal, statutory, or regulatory compliance obligations?',
        answerType: 'yes_no_explanation',
        ccmControl: 'HRS-13',
        ccmControlTitle: 'Compliance User Responsibility',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'IAM',
    name: 'Identity & Access Management',
    description: 'Identity & Access Management',
    questions: [
      {
        id: 'IAM-01.1',
        domain: 'IAM',
        question: 'Are identity and access management policies and procedures established, documented, approved, communicated, implemented, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-01',
        ccmControlTitle: 'Identity and Access Management Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'IAM-01.2',
        domain: 'IAM',
        question: 'Are identity and access management policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-01',
        mapsTo: {}
      },
      {
        id: 'IAM-02.1',
        domain: 'IAM',
        question: 'Are strong password policies and procedures established, documented, approved, communicated, implemented, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-02',
        ccmControlTitle: 'Strong Password Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'IAM-02.2',
        domain: 'IAM',
        question: 'Are strong password policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-02',
        mapsTo: {}
      },
      {
        id: 'IAM-03.1',
        domain: 'IAM',
        question: 'Is system identity information and levels of access managed, stored, and reviewed?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-03',
        ccmControlTitle: 'Identity Inventory',
        mapsTo: {}
      },
      {
        id: 'IAM-04.1',
        domain: 'IAM',
        question: 'Is the separation of duties principle employed when implementing information system access?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-04',
        ccmControlTitle: 'Separation of Duties',
        mapsTo: {}
      },
      {
        id: 'IAM-05.1',
        domain: 'IAM',
        question: 'Is the least privilege principle employed when implementing information system access?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-05',
        ccmControlTitle: 'Least Privilege',
        mapsTo: {}
      },
      {
        id: 'IAM-06.1',
        domain: 'IAM',
        question: 'Is a user access provisioning process defined and implemented which authorizes, records, and communicates data and assets access changes?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-06',
        ccmControlTitle: 'User Access Provisioning',
        mapsTo: {}
      },
      {
        id: 'IAM-07.1',
        domain: 'IAM',
        question: 'Is a process in place to de-provision or modify the access, in a timely manner, of movers / leavers or system identity changes, to effectively adopt and communicate identity and access management policies?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-07',
        ccmControlTitle: 'User Access Changes and Revocation',
        mapsTo: {}
      },
      {
        id: 'IAM-08.1',
        domain: 'IAM',
        question: 'Are reviews and revalidation of user access for least privilege and separation of duties completed with a frequency commensurate with organizational risk tolerance?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-08',
        ccmControlTitle: 'User Access Review',
        mapsTo: {}
      },
      {
        id: 'IAM-09.1',
        domain: 'IAM',
        question: 'Are processes, procedures, and technical measures for the segregation of privileged access roles defined, implemented, and evaluated such that administrative data access, encryption, key management capabilities, and logging capabilities are distinct and separate?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-09',
        ccmControlTitle: 'Segregation of Privileged Access Roles',
        mapsTo: {}
      },
      {
        id: 'IAM-10.1',
        domain: 'IAM',
        question: 'Is an access process defined and implemented to ensure privileged access roles and rights are granted for a limited period?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-10',
        ccmControlTitle: 'Management of Privileged Access Roles',
        mapsTo: {}
      },
      {
        id: 'IAM-10.2',
        domain: 'IAM',
        question: 'Are procedures implemented to prevent the culmination of segregated privileged access?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-10',
        mapsTo: {}
      },
      {
        id: 'IAM-11.1',
        domain: 'IAM',
        question: 'Are processes and procedures for customers to participate, where applicable, in granting access for agreed, high risk as (defined by the organizational risk assessment) privileged access roles defined, implemented and evaluated?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-11',
        ccmControlTitle: 'CSCs Approval for Agreed Privileged Access Roles',
        mapsTo: {}
      },
      {
        id: 'IAM-12.1',
        domain: 'IAM',
        question: 'Are processes, procedures, and technical measures to ensure the logging infrastructure is "read-only" for all with write access (including privileged access roles) defined, implemented, and evaluated?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-12',
        ccmControlTitle: 'Safeguard Logs Integrity',
        mapsTo: {}
      },
      {
        id: 'IAM-12.2',
        domain: 'IAM',
        question: 'Is the ability to disable the "read-only" configuration of logging infrastructure controlled through a procedure that ensures the segregation of duties and break glass procedures?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-12',
        mapsTo: {}
      },
      {
        id: 'IAM-13.1',
        domain: 'IAM',
        question: 'Are processes, procedures, and technical measures that ensure users are identifiable through unique identification (or can associate individuals with user identification usage) defined, implemented, and evaluated?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-13',
        ccmControlTitle: 'Uniquely Identifiable Users',
        mapsTo: {}
      },
      {
        id: 'IAM-14.1',
        domain: 'IAM',
        question: 'Are processes, procedures, and technical measures for authenticating access to systems, application, and data assets including multifactor authentication for a least-privileged user and sensitive data access defined, implemented, and evaluated?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-14',
        ccmControlTitle: 'Strong Authentication',
        mapsTo: {}
      },
      {
        id: 'IAM-14.2',
        domain: 'IAM',
        question: 'Are digital certificates or alternatives that achieve an equivalent security level for system identities adopted?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-14',
        mapsTo: {}
      },
      {
        id: 'IAM-15.1',
        domain: 'IAM',
        question: 'Are processes, procedures, and technical measures for the secure management of passwords defined, implemented, and evaluated?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-15',
        ccmControlTitle: 'Passwords Management',
        mapsTo: {}
      },
      {
        id: 'IAM-16.1',
        domain: 'IAM',
        question: 'Are processes, procedures, and technical measures to verify access to data and system functions authorized, defined, implemented, and evaluated?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IAM-16',
        ccmControlTitle: 'Authorization Mechanisms',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'IPY',
    name: 'Interoperability & Portability',
    description: 'Interoperability & Portability',
    questions: [
      {
        id: 'IPY-01.1',
        domain: 'IPY',
        question: 'Are policies and procedures established, documented, approved, communicated, applied, evaluated, and maintained for communications between application services (e.g., APIs)?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IPY-01',
        ccmControlTitle: 'Interoperability and Portability Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'IPY-01.2',
        domain: 'IPY',
        question: 'Are policies and procedures established, documented, approved, communicated, applied, evaluated, and maintained for information processing interoperability?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IPY-01',
        mapsTo: {}
      },
      {
        id: 'IPY-01.3',
        domain: 'IPY',
        question: 'Are policies and procedures established, documented, approved, communicated, applied, evaluated, and maintained for application development portability?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IPY-01',
        mapsTo: {}
      },
      {
        id: 'IPY-01.4',
        domain: 'IPY',
        question: 'Are policies and procedures established, documented, approved, communicated, applied, evaluated, and maintained for information/data exchange, usage, portability, integrity, and persistence?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IPY-01',
        mapsTo: {}
      },
      {
        id: 'IPY-01.5',
        domain: 'IPY',
        question: 'Are interoperability and portability policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IPY-01',
        mapsTo: {}
      },
      {
        id: 'IPY-02.1',
        domain: 'IPY',
        question: 'Are CSCs able to programmatically retrieve their data via an application interface(s) to enable interoperability and portability?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IPY-02',
        ccmControlTitle: 'Application Interface Availability',
        mapsTo: {}
      },
      {
        id: 'IPY-03.1',
        domain: 'IPY',
        question: 'Are cryptographically secure and standardized network protocols implemented for the management, import, and export of data?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IPY-03',
        ccmControlTitle: 'Secure Interoperability and Portability Management',
        mapsTo: {}
      },
      {
        id: 'IPY-04.1',
        domain: 'IPY',
        question: 'Do agreements include provisions specifying CSC data access upon contract termination, and have the following? a. Data format b. Duration data will be stored c. Scope of the data retained and made available to the CSCs d. Data deletion policy',
        answerType: 'yes_no_explanation',
        ccmControl: 'IPY-04',
        ccmControlTitle: 'Data Portability Contractual Obligations',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'IVS',
    name: 'Infrastructure & Virtualization Security',
    description: 'Infrastructure & Virtualization Security',
    questions: [
      {
        id: 'IVS-01.1',
        domain: 'IVS',
        question: 'Are infrastructure and virtualization security policies and procedures established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IVS-01',
        ccmControlTitle: 'Infrastructure and Virtualization Security Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'IVS-01.2',
        domain: 'IVS',
        question: 'Are infrastructure and virtualization security policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IVS-01',
        mapsTo: {}
      },
      {
        id: 'IVS-02.1',
        domain: 'IVS',
        question: 'Is resource availability, quality, and capacity planned and monitored in a way that delivers required system performance, as determined by the business?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IVS-02',
        ccmControlTitle: 'Capacity and Resource Planning',
        mapsTo: {}
      },
      {
        id: 'IVS-03.1',
        domain: 'IVS',
        question: 'Are communications between environments monitored?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IVS-03',
        ccmControlTitle: 'Network Security',
        mapsTo: {}
      },
      {
        id: 'IVS-03.2',
        domain: 'IVS',
        question: 'Are communications between environments encrypted?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IVS-03',
        mapsTo: {}
      },
      {
        id: 'IVS-03.3',
        domain: 'IVS',
        question: 'Are communications between environments restricted to only authenticated and authorized connections, as justified by the business?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IVS-03',
        mapsTo: {}
      },
      {
        id: 'IVS-03.4',
        domain: 'IVS',
        question: 'Are network configurations reviewed at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IVS-03',
        mapsTo: {}
      },
      {
        id: 'IVS-03.5',
        domain: 'IVS',
        question: 'Are network configurations supported by the documented justification of all allowed services, protocols, ports, and compensating controls?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IVS-03',
        mapsTo: {}
      },
      {
        id: 'IVS-04.1',
        domain: 'IVS',
        question: 'Is every host and guest OS, hypervisor, or infrastructure control plane hardened (according to their respective best practices) and supported by technical controls as part of a security baseline?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IVS-04',
        ccmControlTitle: 'OS Hardening and Base Controls',
        mapsTo: {}
      },
      {
        id: 'IVS-05.1',
        domain: 'IVS',
        question: 'Are production and non-production environments separated?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IVS-05',
        ccmControlTitle: 'Production and Non-Production Environments',
        mapsTo: {}
      },
      {
        id: 'IVS-06.1',
        domain: 'IVS',
        question: 'Are applications and infrastructures designed, developed, deployed, and configured such that CSP and CSC (tenant) user access and intra-tenant access is appropriately segmented, segregated, monitored, and restricted from other tenants?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IVS-06',
        ccmControlTitle: 'Segmentation and Segregation',
        mapsTo: {}
      },
      {
        id: 'IVS-07.1',
        domain: 'IVS',
        question: 'Are secure and encrypted communication channels including only up-to-date and approved protocols used when migrating servers, services, applications, or data to cloud environments?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IVS-07',
        ccmControlTitle: 'Migration to Cloud Environments',
        mapsTo: {}
      },
      {
        id: 'IVS-08.1',
        domain: 'IVS',
        question: 'Are high-risk environments identified and documented?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IVS-08',
        ccmControlTitle: 'Network Architecture Documentation',
        mapsTo: {}
      },
      {
        id: 'IVS-09.1',
        domain: 'IVS',
        question: 'Are processes, procedures, and defense-in-depth techniques defined, implemented, and evaluated for protection, detection, and timely response to network-based attacks?',
        answerType: 'yes_no_explanation',
        ccmControl: 'IVS-09',
        ccmControlTitle: 'Network Defense',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'LOG',
    name: 'Logging and Monitoring',
    description: 'Logging and Monitoring',
    questions: [
      {
        id: 'LOG-01.1',
        domain: 'LOG',
        question: 'Are logging and monitoring policies and procedures established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-01',
        ccmControlTitle: 'Logging and Monitoring Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'LOG-01.2',
        domain: 'LOG',
        question: 'Are policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-01',
        mapsTo: {}
      },
      {
        id: 'LOG-02.1',
        domain: 'LOG',
        question: 'Are processes, procedures, and technical measures defined, implemented, and evaluated to ensure audit log security and retention?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-02',
        ccmControlTitle: 'Audit Logs Protection',
        mapsTo: {}
      },
      {
        id: 'LOG-03.1',
        domain: 'LOG',
        question: 'Are security-related events identified and monitored within applications and the underlying infrastructure?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-03',
        ccmControlTitle: 'Security Monitoring and Alerting',
        mapsTo: {}
      },
      {
        id: 'LOG-03.2',
        domain: 'LOG',
        question: 'Is a system defined and implemented to generate alerts to responsible stakeholders based on security events and their corresponding metrics?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-03',
        mapsTo: {}
      },
      {
        id: 'LOG-04.1',
        domain: 'LOG',
        question: 'Is access to audit logs restricted to authorized personnel, and are records maintained to provide unique access accountability?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-04',
        ccmControlTitle: 'Audit Logs Access and Accountability',
        mapsTo: {}
      },
      {
        id: 'LOG-05.1',
        domain: 'LOG',
        question: 'Are security audit logs monitored to detect activity outside of typical or expected patterns?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-05',
        ccmControlTitle: 'Audit Logs Monitoring and Response',
        mapsTo: {}
      },
      {
        id: 'LOG-05.2',
        domain: 'LOG',
        question: 'Is a process established and followed to review and take appropriate and timely actions on detected anomalies?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-05',
        mapsTo: {}
      },
      {
        id: 'LOG-06.1',
        domain: 'LOG',
        question: 'Is a reliable time source being used across all relevant information processing systems?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-06',
        ccmControlTitle: 'Clock Synchronization',
        mapsTo: {}
      },
      {
        id: 'LOG-07.1',
        domain: 'LOG',
        question: 'Are logging requirements for information meta/data system events established, documented, and implemented?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-07',
        ccmControlTitle: 'Logging Scope',
        mapsTo: {}
      },
      {
        id: 'LOG-07.2',
        domain: 'LOG',
        question: 'Is the scope reviewed and updated at least annually, or whenever there is a change in the threat environment?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-07',
        mapsTo: {}
      },
      {
        id: 'LOG-08.1',
        domain: 'LOG',
        question: 'Are audit records generated, and do they contain relevant security information?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-08',
        ccmControlTitle: 'Log Records',
        mapsTo: {}
      },
      {
        id: 'LOG-09.1',
        domain: 'LOG',
        question: 'Does the information system protect audit records from unauthorized access, modification, and deletion?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-09',
        ccmControlTitle: 'Log Protection',
        mapsTo: {}
      },
      {
        id: 'LOG-10.1',
        domain: 'LOG',
        question: 'Are monitoring and internal reporting capabilities established to report on cryptographic operations, encryption, and key management policies, processes, procedures, and controls?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-10',
        ccmControlTitle: 'Encryption Monitoring and Reporting',
        mapsTo: {}
      },
      {
        id: 'LOG-11.1',
        domain: 'LOG',
        question: 'Are key lifecycle management events logged and monitored to enable auditing and reporting on cryptographic keys\' usage?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-11',
        ccmControlTitle: 'Transaction/Activity Logging',
        mapsTo: {}
      },
      {
        id: 'LOG-12.1',
        domain: 'LOG',
        question: 'Is physical access logged and monitored using an auditable access control system?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-12',
        ccmControlTitle: 'Access Control Logs',
        mapsTo: {}
      },
      {
        id: 'LOG-13.1',
        domain: 'LOG',
        question: 'Are processes and technical measures for reporting monitoring system anomalies and failures defined, implemented, and evaluated?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-13',
        ccmControlTitle: 'Failures and Anomalies Reporting',
        mapsTo: {}
      },
      {
        id: 'LOG-13.2',
        domain: 'LOG',
        question: 'Are accountable parties immediately notified about anomalies and failures?',
        answerType: 'yes_no_explanation',
        ccmControl: 'LOG-13',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'SEF',
    name: 'Security Incident Management, E-Discovery, & Cloud Forensics',
    description: 'Security Incident Management, E-Discovery, & Cloud Forensics',
    questions: [
      {
        id: 'SEF-01.1',
        domain: 'SEF',
        question: 'Are policies and procedures for security incident management, e-discovery, and cloud forensics established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'SEF-01',
        ccmControlTitle: 'Security Incident Management Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'SEF-01.2',
        domain: 'SEF',
        question: 'Are policies and procedures reviewed and updated annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'SEF-01',
        mapsTo: {}
      },
      {
        id: 'SEF-02.1',
        domain: 'SEF',
        question: 'Are policies and procedures for timely management of security incidents established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'SEF-02',
        ccmControlTitle: 'Service Management Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'SEF-02.2',
        domain: 'SEF',
        question: 'Are policies and procedures for timely management of security incidents reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'SEF-02',
        mapsTo: {}
      },
      {
        id: 'SEF-03.1',
        domain: 'SEF',
        question: 'Is a security incident response plan that includes relevant internal departments, impacted CSCs, and other business-critical relationships (such as supply-chain) established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'SEF-03',
        ccmControlTitle: 'Incident Response Plans',
        mapsTo: {}
      },
      {
        id: 'SEF-04.1',
        domain: 'SEF',
        question: 'Is the security incident response plan tested and updated for effectiveness, as necessary, at planned intervals or upon significant organizational or environmental changes?',
        answerType: 'yes_no_explanation',
        ccmControl: 'SEF-04',
        ccmControlTitle: 'Incident Response Testing',
        mapsTo: {}
      },
      {
        id: 'SEF-05.1',
        domain: 'SEF',
        question: 'Are information security incident metrics established and monitored?',
        answerType: 'yes_no_explanation',
        ccmControl: 'SEF-05',
        ccmControlTitle: 'Incident Response Metrics',
        mapsTo: {}
      },
      {
        id: 'SEF-06.1',
        domain: 'SEF',
        question: 'Are processes, procedures, and technical measures supporting business processes to triage security-related events defined, implemented, and evaluated?',
        answerType: 'yes_no_explanation',
        ccmControl: 'SEF-06',
        ccmControlTitle: 'Event Triage Processes',
        mapsTo: {}
      },
      {
        id: 'SEF-07.1',
        domain: 'SEF',
        question: 'Are processes, procedures, and technical measures for security breach notifications defined and implemented?',
        answerType: 'yes_no_explanation',
        ccmControl: 'SEF-07',
        ccmControlTitle: 'Security Breach Notification',
        mapsTo: {}
      },
      {
        id: 'SEF-07.2',
        domain: 'SEF',
        question: 'Are security breaches and assumed security breaches reported (including any relevant supply chain breaches) as per applicable SLAs, laws, and regulations?',
        answerType: 'yes_no_explanation',
        ccmControl: 'SEF-07',
        mapsTo: {}
      },
      {
        id: 'SEF-08.1',
        domain: 'SEF',
        question: 'Are points of contact maintained for applicable regulation authorities, national and local law enforcement, and other legal jurisdictional authorities?',
        answerType: 'yes_no_explanation',
        ccmControl: 'SEF-08',
        ccmControlTitle: 'Points of Contact Maintenance',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'STA',
    name: 'Supply Chain Management, Transparency, and Accountability',
    description: 'Supply Chain Management, Transparency, and Accountability',
    questions: [
      {
        id: 'STA-01.1',
        domain: 'STA',
        question: 'Are policies and procedures implementing the shared security responsibility model (SSRM) within the organization established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-01',
        ccmControlTitle: 'SSRM Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'STA-01.2',
        domain: 'STA',
        question: 'Are the policies and procedures that apply the SSRM reviewed and updated annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-01',
        mapsTo: {}
      },
      {
        id: 'STA-02.1',
        domain: 'STA',
        question: 'Is the SSRM applied, documented, implemented, and managed throughout the supply chain for the cloud service offering?',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-02',
        ccmControlTitle: 'SSRM Supply Chain',
        mapsTo: {}
      },
      {
        id: 'STA-03.1',
        domain: 'STA',
        question: 'Is the CSC given SSRM guidance detailing information about SSRM applicability throughout the supply chain?',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-03',
        ccmControlTitle: 'SSRM Guidance',
        mapsTo: {}
      },
      {
        id: 'STA-04.1',
        domain: 'STA',
        question: 'Is the shared ownership and applicability of all CSA CCM controls delineated according to the SSRM for the cloud service offering?',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-04',
        ccmControlTitle: 'SSRM Control Ownership',
        mapsTo: {}
      },
      {
        id: 'STA-05.1',
        domain: 'STA',
        question: 'Is SSRM documentation for all cloud services the organization uses reviewed and validated?',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-05',
        ccmControlTitle: 'SSRM Documentation Review',
        mapsTo: {}
      },
      {
        id: 'STA-06.1',
        domain: 'STA',
        question: 'Are the portions of the SSRM the organization is responsible for implemented, operated, audited, or assessed?',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-06',
        ccmControlTitle: 'SSRM Control Implementation',
        mapsTo: {}
      },
      {
        id: 'STA-07.1',
        domain: 'STA',
        question: 'Is an inventory of all supply chain relationships developed and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-07',
        ccmControlTitle: 'Supply Chain Inventory',
        mapsTo: {}
      },
      {
        id: 'STA-08.1',
        domain: 'STA',
        question: 'Are risk factors associated with all organizations within the supply chain periodically reviewed by CSPs?',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-08',
        ccmControlTitle: 'Supply Chain Risk Management',
        mapsTo: {}
      },
      {
        id: 'STA-09.1',
        domain: 'STA',
        question: 'Do service agreements between CSPs and CSCs (tenants) incorporate at least the following mutually agreed upon provisions and/or terms? • Scope, characteristics, and location of business relationship and services offered • Information security requirements (including SSRM) • Change management process • Logging and monitoring capability • Incident management and communication procedures • Right to audit and third-party assessment • Service termination • Interoperability and portability requirements • Data privacy',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-09',
        ccmControlTitle: 'Primary Service and Contractual Agreement',
        mapsTo: {}
      },
      {
        id: 'STA-10.1',
        domain: 'STA',
        question: 'Are supply chain agreements between CSPs and CSCs reviewed at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-10',
        ccmControlTitle: 'Supply Chain Agreement Review',
        mapsTo: {}
      },
      {
        id: 'STA-11.1',
        domain: 'STA',
        question: 'Is there a process for conducting internal assessments at least annually to confirm the conformance and effectiveness of standards, policies, procedures, and SLA activities?',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-11',
        ccmControlTitle: 'Internal Compliance Testing',
        mapsTo: {}
      },
      {
        id: 'STA-12.1',
        domain: 'STA',
        question: 'Are policies that require all supply chain CSPs to comply with information security, confidentiality, access control, privacy, audit, personnel policy, and service level requirements and standards implemented?',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-12',
        ccmControlTitle: 'Supply Chain Service Agreement Compliance',
        mapsTo: {}
      },
      {
        id: 'STA-13.1',
        domain: 'STA',
        question: 'Are supply chain partner IT governance policies and procedures reviewed periodically?',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-13',
        ccmControlTitle: 'Supply Chain Governance Review',
        mapsTo: {}
      },
      {
        id: 'STA-14.1',
        domain: 'STA',
        question: 'Is a process to conduct periodic security assessments for all supply chain organizations defined and implemented?',
        answerType: 'yes_no_explanation',
        ccmControl: 'STA-14',
        ccmControlTitle: 'Supply Chain Data Security Assessment',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'TVM',
    name: 'Threat & Vulnerability Management',
    description: 'Threat & Vulnerability Management',
    questions: [
      {
        id: 'TVM-01.1',
        domain: 'TVM',
        question: 'Are policies and procedures established, documented, approved, communicated, applied, evaluated, and maintained to identify, report, and prioritize the remediation of vulnerabilities to protect systems against vulnerability exploitation?',
        answerType: 'yes_no_explanation',
        ccmControl: 'TVM-01',
        ccmControlTitle: 'Threat and Vulnerability Management Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'TVM-01.2',
        domain: 'TVM',
        question: 'Are threat and vulnerability management policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'TVM-01',
        mapsTo: {}
      },
      {
        id: 'TVM-02.1',
        domain: 'TVM',
        question: 'Are policies and procedures to protect against malware on managed assets established, documented, approved, communicated, applied, evaluated, and maintained?',
        answerType: 'yes_no_explanation',
        ccmControl: 'TVM-02',
        ccmControlTitle: 'Malware Protection Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'TVM-02.2',
        domain: 'TVM',
        question: 'Are asset management and malware protection policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'TVM-02',
        mapsTo: {}
      },
      {
        id: 'TVM-03.1',
        domain: 'TVM',
        question: 'Are processes, procedures, and technical measures defined, implemented, and evaluated to enable scheduled and emergency responses to vulnerability identifications (based on the identified risk)?',
        answerType: 'yes_no_explanation',
        ccmControl: 'TVM-03',
        ccmControlTitle: 'Vulnerability Remediation Schedule',
        mapsTo: {}
      },
      {
        id: 'TVM-04.1',
        domain: 'TVM',
        question: 'Are processes, procedures, and technical measures defined, implemented, and evaluated to update detection tools, threat signatures, and compromise indicators weekly (or more frequent) basis?',
        answerType: 'yes_no_explanation',
        ccmControl: 'TVM-04',
        ccmControlTitle: 'Detection Updates',
        mapsTo: {}
      },
      {
        id: 'TVM-05.1',
        domain: 'TVM',
        question: 'Are processes, procedures, and technical measures defined, implemented, and evaluated to identify updates for applications that use third-party or open-source libraries (according to the organization\'s vulnerability management policy)?',
        answerType: 'yes_no_explanation',
        ccmControl: 'TVM-05',
        ccmControlTitle: 'External Library Vulnerabilities',
        mapsTo: {}
      },
      {
        id: 'TVM-06.1',
        domain: 'TVM',
        question: 'Are processes, procedures, and technical measures defined, implemented, and evaluated for periodic, independent, third-party penetration testing?',
        answerType: 'yes_no_explanation',
        ccmControl: 'TVM-06',
        ccmControlTitle: 'Penetration Testing',
        mapsTo: {}
      },
      {
        id: 'TVM-07.1',
        domain: 'TVM',
        question: 'Are processes, procedures, and technical measures defined, implemented, and evaluated for vulnerability detection on organizationally managed assets at least monthly?',
        answerType: 'yes_no_explanation',
        ccmControl: 'TVM-07',
        ccmControlTitle: 'Vulnerability Identification',
        mapsTo: {}
      },
      {
        id: 'TVM-08.1',
        domain: 'TVM',
        question: 'Is vulnerability remediation prioritized using a risk-based model from an industry-recognized framework?',
        answerType: 'yes_no_explanation',
        ccmControl: 'TVM-08',
        ccmControlTitle: 'Vulnerability Prioritization',
        mapsTo: {}
      },
      {
        id: 'TVM-09.1',
        domain: 'TVM',
        question: 'Is a process defined and implemented to track and report vulnerability identification and remediation activities that include stakeholder notification?',
        answerType: 'yes_no_explanation',
        ccmControl: 'TVM-09',
        ccmControlTitle: 'Vulnerability Management Reporting',
        mapsTo: {}
      },
      {
        id: 'TVM-10.1',
        domain: 'TVM',
        question: 'Are metrics for vulnerability identification and remediation established, monitored, and reported at defined intervals?',
        answerType: 'yes_no_explanation',
        ccmControl: 'TVM-10',
        ccmControlTitle: 'Vulnerability Management Metrics',
        mapsTo: {}
      }
    ]
  },
  {
    id: 'UEM',
    name: 'Universal Endpoint Management',
    description: 'Universal Endpoint Management',
    questions: [
      {
        id: 'UEM-01.1',
        domain: 'UEM',
        question: 'Are policies and procedures established, documented, approved, communicated, applied, evaluated, and maintained for all endpoints?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-01',
        ccmControlTitle: 'Endpoint Devices Policy and Procedures',
        mapsTo: {}
      },
      {
        id: 'UEM-01.2',
        domain: 'UEM',
        question: 'Are universal endpoint management policies and procedures reviewed and updated at least annually?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-01',
        mapsTo: {}
      },
      {
        id: 'UEM-02.1',
        domain: 'UEM',
        question: 'Is there a defined, documented, applicable and evaluated list containing approved services, applications, and the sources of applications (stores) acceptable for use by endpoints when accessing or storing organization-managed data?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-02',
        ccmControlTitle: 'Application and Service Approval',
        mapsTo: {}
      },
      {
        id: 'UEM-03.1',
        domain: 'UEM',
        question: 'Is a process defined and implemented to validate endpoint device compatibility with operating systems and applications?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-03',
        ccmControlTitle: 'Compatibility',
        mapsTo: {}
      },
      {
        id: 'UEM-04.1',
        domain: 'UEM',
        question: 'Is an inventory of all endpoints used and maintained to store and access company data?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-04',
        ccmControlTitle: 'Endpoint Inventory',
        mapsTo: {}
      },
      {
        id: 'UEM-05.1',
        domain: 'UEM',
        question: 'Are processes, procedures, and technical measures defined, implemented and evaluated, to enforce policies and controls for all endpoints permitted to access systems and/or store, transmit, or process organizational data?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-05',
        ccmControlTitle: 'Endpoint Management',
        mapsTo: {}
      },
      {
        id: 'UEM-06.1',
        domain: 'UEM',
        question: 'Are all relevant interactive-use endpoints configured to require an automatic lock screen?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-06',
        ccmControlTitle: 'Automatic Lock Screen',
        mapsTo: {}
      },
      {
        id: 'UEM-07.1',
        domain: 'UEM',
        question: 'Are changes to endpoint operating systems, patch levels, and/or applications managed through the organizational change management process?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-07',
        ccmControlTitle: 'Operating Systems',
        mapsTo: {}
      },
      {
        id: 'UEM-08.1',
        domain: 'UEM',
        question: 'Is information protected from unauthorized disclosure on managed endpoints with storage encryption?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-08',
        ccmControlTitle: 'Storage Encryption',
        mapsTo: {}
      },
      {
        id: 'UEM-09.1',
        domain: 'UEM',
        question: 'Are anti-malware detection and prevention technology services configured on managed endpoints?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-09',
        ccmControlTitle: 'Anti-Malware Detection and Prevention',
        mapsTo: {}
      },
      {
        id: 'UEM-10.1',
        domain: 'UEM',
        question: 'Are software firewalls configured on managed endpoints?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-10',
        ccmControlTitle: 'Software Firewall',
        mapsTo: {}
      },
      {
        id: 'UEM-11.1',
        domain: 'UEM',
        question: 'Are managed endpoints configured with data loss prevention (DLP) technologies and rules per a risk assessment?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-11',
        ccmControlTitle: 'Data Loss Prevention',
        mapsTo: {}
      },
      {
        id: 'UEM-12.1',
        domain: 'UEM',
        question: 'Are remote geolocation capabilities enabled for all managed mobile endpoints?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-12',
        ccmControlTitle: 'Remote Locate',
        mapsTo: {}
      },
      {
        id: 'UEM-13.1',
        domain: 'UEM',
        question: 'Are processes, procedures, and technical measures defined, implemented, and evaluated to enable remote company data deletion on managed endpoint devices?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-13',
        ccmControlTitle: 'Remote Wipe',
        mapsTo: {}
      },
      {
        id: 'UEM-14.1',
        domain: 'UEM',
        question: 'Are processes, procedures, and technical and/or contractual measures defined, implemented, and evaluated to maintain proper security of third-party endpoints with access to organizational assets?',
        answerType: 'yes_no_explanation',
        ccmControl: 'UEM-14',
        ccmControlTitle: 'Third-Party Endpoint Security Posture',
        mapsTo: {}
      }
    ]
  }
]

export const CAIQ_VERSION = '4.0.3'
export const TOTAL_QUESTIONS = CAIQ_V4.reduce((sum, domain) => sum + domain.questions.length, 0)
